/* Customer Portal pages */
import React, { useEffect, useState } from "react";
import StorefrontLayout from "../../components/storefront/StorefrontLayout";
import { api, API_BASE } from "../../lib/api";
import { useAuth } from "../../lib/auth";
import { fmtINR } from "../../lib/constants";
import { Download, ShieldOff } from "lucide-react";
import { toast } from "sonner";
import { Link, useNavigate } from "react-router-dom";

export function MyOrdersPage() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [locks, setLocks] = useState([]);
  useEffect(() => {
    if (user) {
      api.get("/api/orders/mine").then((r) => setItems(r.data || []));
      api.get("/api/lock-rate/mine").then((r) => setLocks(r.data || [])).catch(() => {});
    }
  }, [user]);
  const downloadInvoice = async (id) => {
    const t = localStorage.getItem("sgpl_token");
    const resp = await fetch(`${API_BASE}/orders/${id}/invoice`, { headers: { Authorization: `Bearer ${t}` } });
    if (!resp.ok) { toast.error("Invoice not ready"); return; }
    const blob = await resp.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url; a.download = `SGPL-Invoice-${id.slice(0,8)}.pdf`; a.click();
  };
  if (!user) return <StorefrontLayout><div className="p-12 text-center"><Link to="/login" className="btn-maroon">Login</Link></div></StorefrontLayout>;
  return (
    <StorefrontLayout>
      <div className="px-6 lg:px-12 py-10 max-w-4xl mx-auto">
        <h1 className="font-display text-4xl text-maroon">My Orders</h1>
        <div className="royal-divider my-6"/>
        {items.length === 0 ? <div className="text-center py-20 text-ash">No orders yet.</div> : (
          <div className="space-y-3">
            {items.map((o) => (
              <div key={o.id} className="bg-white border border-line p-4 flex items-center gap-4" data-testid={`my-order-${o.id}`}>
                <div className="flex-1">
                  <div className="font-medium">Order #{o.id.slice(0,8)}</div>
                  <div className="text-xs text-ash">{new Date(o.created_at).toLocaleDateString()} · {o.items?.length} item(s) · <span className={o.payment_status === "paid" ? "text-green-700" : "text-ash"}>{o.payment_status}</span></div>
                </div>
                <div className="text-right">
                  <div className="font-display text-xl text-maroon">{fmtINR(o.total)}</div>
                  {o.payment_status === "paid" && (
                    <button onClick={() => downloadInvoice(o.id)} className="text-xs text-maroon underline flex items-center gap-1 mt-1 ml-auto" data-testid={`invoice-${o.id}`}>
                      <Download size={11}/> GST Invoice
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Rate-lock token bookings */}
        <h2 className="font-display text-3xl text-maroon mt-12">My Rate Locks</h2>
        <div className="royal-divider my-6"/>
        {locks.length === 0 ? <div className="text-center py-10 text-ash text-sm">No rate-lock tokens yet.</div> : (
          <div className="space-y-3">
            {locks.map((b) => (
              <div key={b.id} className="bg-white border border-line p-4" data-testid={`my-lock-${b.id}`}>
                <div className="flex items-center justify-between">
                  <div>
                    <div className="font-medium">{b.product_name || "Rate lock"} · {b.desired_purity} · {b.desired_weight}g</div>
                    <div className="text-xs text-ash mt-0.5">Locked at ₹{b.locked_rate_per_g}/g · valid until {b.valid_until?.slice(0, 10)}</div>
                  </div>
                  <div className="text-right">
                    <div className="font-display text-xl text-maroon">{fmtINR(b.token_amount)}</div>
                    <span className={`text-[10px] uppercase tracking-widest ${b.payment_status === "paid" ? "text-green-700" : "text-ash"}`}>{b.payment_status || b.status}</span>
                  </div>
                </div>
                {b.transaction_id && (
                  <div className="text-[11px] text-ash mt-2 pt-2 border-t border-line/60">
                    Transaction ID: {b.transaction_id} · Paid on {b.paid_at ? new Date(b.paid_at).toLocaleString() : "—"}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </StorefrontLayout>
  );
}

export function MyAccountPage() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const deleteAccount = async () => {
    if (!window.confirm("DPDP: This will anonymise your account. Order history is retained 7 years for tax compliance. Continue?")) return;
    await api.post("/api/privacy/delete-my-account");
    logout(); toast.success("Account anonymised"); nav("/");
  };
  if (!user) return <StorefrontLayout><div className="p-12 text-center"><Link to="/login" className="btn-maroon">Login</Link></div></StorefrontLayout>;
  return (
    <StorefrontLayout>
      <div className="px-6 lg:px-12 py-10 max-w-2xl mx-auto">
        <h1 className="font-display text-4xl text-maroon">My Account</h1>
        <div className="royal-divider my-6"/>
        <div className="bg-white border border-line p-6 space-y-2">
          <div><span className="eyebrow text-[10px]">Name</span><div className="text-sm">{user.name}</div></div>
          <div><span className="eyebrow text-[10px]">Email</span><div className="text-sm">{user.email}</div></div>
          <div><span className="eyebrow text-[10px]">Phone</span><div className="text-sm">{user.phone || "—"}</div></div>
        </div>
        <div className="grid md:grid-cols-2 gap-3 mt-4">
          <Link to="/my-orders" className="bg-white border border-line p-4 hover-lift" data-testid="link-my-orders">My Orders</Link>
          <Link to="/wishlist" className="bg-white border border-line p-4 hover-lift" data-testid="link-my-wishlist">My Wishlist</Link>
        </div>
        <div className="mt-10 p-4 border border-line bg-pearl">
          <div className="eyebrow text-[10px] mb-2 flex items-center gap-2"><ShieldOff size={14}/> DPDP — Right to be forgotten</div>
          <p className="text-xs text-ash">As per the Digital Personal Data Protection Act, you can request deletion of your personal data. Order history will be retained for tax compliance (7 years) but anonymised.</p>
          <button onClick={deleteAccount} className="text-xs text-red-700 underline mt-2" data-testid="delete-account-btn">Delete my account</button>
        </div>
      </div>
    </StorefrontLayout>
  );
}

export function PrivacyPage() {
  return (
    <StorefrontLayout>
      <div className="px-6 lg:px-12 py-12 max-w-3xl mx-auto">
        <div className="eyebrow">Trust</div>
        <h1 className="font-display text-5xl text-maroon mt-2">Privacy Policy & DPDP Compliance</h1>
        <div className="royal-divider my-8"/>
        <div className="prose space-y-4 text-ink text-sm leading-relaxed">
          <p>Swayambhu Gold Private Limited respects your privacy and is committed to compliance with the Digital Personal Data Protection Act, 2023 of India.</p>
          <h3 className="font-display text-2xl text-maroon">What we collect</h3>
          <p>Name, email, phone, shipping address, payment metadata, browsing on this site, and (with consent) WhatsApp marketing preferences.</p>
          <h3 className="font-display text-2xl text-maroon">Your rights</h3>
          <ul className="list-disc pl-6">
            <li>Right to access, correct, erase your data</li>
            <li>Right to withdraw consent at any time</li>
            <li>Right to data portability</li>
            <li>Right to grievance redressal</li>
          </ul>
          <h3 className="font-display text-2xl text-maroon">Data retention</h3>
          <p>Order and tax records are retained for 7 years per Indian tax law. All other personal data is anonymised upon account deletion request.</p>
          <p>Grievance Officer: care@swayambhugold.com · +91 98765 43210</p>
        </div>
      </div>
    </StorefrontLayout>
  );
}
